import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {BankService} from './customer/bank.service';
import {TixxConnection} from '../models';
import {environment} from '~environments/environment';
import {Customer} from '../models';

/**
 * Is responsible for fetching Tixx data for the customer and client. Displays web shop in iframe.
 */
@Injectable({
  providedIn: 'root',
})
export class TixxConnectionService {
  public API_URL: any = `${environment.envAPI}`;
  public isBankSelectModalOpened: boolean = false;
  public temporaryReservationId: string;
  private selectedBankId: number = null;
  private tixxInitiator: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private tixxConnectionEstablished: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public i18nErrorCouldNotEstablishTixx: any = $localize`:@@alertService.warning.couldNotEstablishTixx:Could not establish connection to Tixx. Define Tixx connection properties on the club-overview.`;
  public i18nErrorVerifyConnectionTixx: any = $localize`:@@alertService.error.verifyConnectionTixx:Connection to Tixx could not be initiated. Please verify the connection and user settings in Tixx.`;


  constructor(private http: HttpClient, private bankService: BankService) {
  }

  /**
   * Returns tixx connection data.
   */
  public getData(customer: Customer, bankAccountId?: number, reservationId: string = null): Observable<TixxConnection> {
    let queryParams: HttpParams = new HttpParams();

    if (bankAccountId !== null) {
      queryParams = queryParams.append('bankAccountId', String(bankAccountId));
    }

    if (reservationId !== null) {
      queryParams = queryParams.append('reservationId', String(reservationId));
    }

    return this.http.get<TixxConnection>(`${this.API_URL}/customers/${customer.id}/tixx-connection`, {params: queryParams});
  }

  /**
   * Creates the HTML form and submits it. Displays the response in an iframe.
   */
  public openTixxIframe(tixxData: TixxConnection): void {
    const appMainView = window.document.getElementById('tixx-webshop-overlay');

    const form = window.document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('action', tixxData.url);
    form.setAttribute('target', 'tixxWebshop');

    form.appendChild(this.createHiddenElement('client', tixxData.client));
    form.appendChild(this.createHiddenElement('user', tixxData.username));
    form.appendChild(this.createHiddenElement('pw', tixxData.password));
    form.appendChild(this.createHiddenElement('xml_content', tixxData.xmlContent));
    appMainView.appendChild(form);

    const iframe = window.document.createElement('iframe');
    iframe.setAttribute('name', 'tixxWebshop');
    appMainView.appendChild(iframe);

    form.submit();
  }

  /**
   * Create a hidden form input element.
   */
  private createHiddenElement(name: string, value: string): HTMLElement {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('name', name);
    hiddenField.setAttribute('value', value);
    hiddenField.setAttribute('type', 'hidden');

    return hiddenField;
  }

  /**
   * If the Customer has more than 1 bank account, open the modal to select bank account, otherwise open Tixx.
   */
  public initiateTixxConnection(customer: Customer, reservationId: string = null): void {
    this.bankService.clearBankDataUpdate();
    this.temporaryReservationId = reservationId;
    this.selectedBankId = customer.bankAccounts.length > 0 ? customer.bankAccounts[0].id : null;
    if (customer.bankAccounts.length > 1) {
      this.toggleBankSelectModalOpened(true);
    } else {
      this.openTixxWebshop();
    }
  }

  /**
   * Close the modal and announce to service what bank id you will use.
   */
  public openTixxWebshop(): void {
    this.announceTixxConnection(this.selectedBankId, this.temporaryReservationId);
    this.toggleBankSelectModalOpened(false);
    this.temporaryReservationId = undefined;
  }

  /**
   * Notifies tixx connection component to create tixx connection.
   */
  public announceTixxConnection(bankAccountId: number, reservationId: string): void {
    this.tixxInitiator.next({open: true, bankAccountId, reservationId});
  }

  /**
   * Returns the observable. Subscribe to this observable if you want to get the values.
   */
  public getTixxConnection(): Observable<object> {
    return this.tixxInitiator;
  }

  /**
   * Close the connection.
   */
  public clearTixxConnection(): void {
    this.tixxInitiator.next(null);
  }

  /**
   * Notifies tixx connection component to create tixx connection.
   */
  public announceTixxConnectionEstablished(): void {
    this.tixxConnectionEstablished.next(true);
  }

  /**
   * Returns the observable. Subscribe to this observable if you want to get the values.
   */
  public getTixxConnectionEstablished(): Observable<boolean> {
    return this.tixxConnectionEstablished;
  }

  /**
   * Close the connection.
   */
  public clearTixxEstablishedConnection(): void {
    this.tixxConnectionEstablished.next(null);
  }

  /**
   * Updates the status of the bank select modal
   */
  public toggleBankSelectModalOpened(status: boolean): void {
    this.isBankSelectModalOpened = status;
  }

  /**
   * Updates the selected bank id
   */
  public setSelectedBankId(id: number): void {
    this.selectedBankId = id;
  }
}
